import { useEffect } from 'react'
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom'
import { GoogleOAuthProvider } from '@react-oauth/google'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { useAuthStore, LanguageProvider } from '@eshop/shared'
import { AdminLogin } from './pages/AdminLogin'
import { AdminDashboard } from './pages/AdminDashboard'
import { VendorsPage } from './pages/VendorsPage'
import { AdminProductsPage } from './pages/AdminProductsPage'
import { AdminOrdersPage } from './pages/AdminOrdersPage'
import { AdminCategoriesPage } from './pages/AdminCategoriesPage'
import { StaffPage } from './pages/StaffPage'
import { KYCReviewPage } from './pages/KYCReviewPage'
import './styles/globals.css'

const queryClient = new QueryClient()
const GOOGLE_CLIENT_ID = import.meta.env.VITE_GOOGLE_CLIENT_ID

if (!GOOGLE_CLIENT_ID) {
  console.error('❌ ERROR: VITE_GOOGLE_CLIENT_ID is not configured!')
  console.error('Make sure .env has: VITE_GOOGLE_CLIENT_ID=...')
}

function AdminRoute({ children }: { children: React.ReactNode }) {
  const { user, isAuthenticated, isLoading } = useAuthStore()

  if (isLoading) {
    return <div>Loading...</div>
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />
  }

  if (!user?.is_staff) {
    return <Navigate to="/login" replace />
  }

  return <>{children}</>
}

function App() {
  const { checkAuth } = useAuthStore()

  useEffect(() => {
    checkAuth()
  }, [checkAuth])

  return (
    <GoogleOAuthProvider clientId={GOOGLE_CLIENT_ID}>
      <QueryClientProvider client={queryClient}>
        <LanguageProvider>
          <BrowserRouter>
            <Routes>
              <Route path="/login" element={<AdminLogin />} />
              <Route path="/dashboard" element={<AdminRoute><AdminDashboard /></AdminRoute>} />
              <Route path="/vendors" element={<AdminRoute><VendorsPage /></AdminRoute>} />
              <Route path="/products" element={<AdminRoute><AdminProductsPage /></AdminRoute>} />
              <Route path="/categories" element={<AdminRoute><AdminCategoriesPage /></AdminRoute>} />
              <Route path="/orders" element={<AdminRoute><AdminOrdersPage /></AdminRoute>} />
              <Route path="/staff" element={<AdminRoute><StaffPage /></AdminRoute>} />
              <Route path="/kyc" element={<AdminRoute><KYCReviewPage /></AdminRoute>} />
              <Route path="/" element={<Navigate to="/dashboard" replace />} />
              <Route path="*" element={<Navigate to="/login" replace />} />
            </Routes>
          </BrowserRouter>
        </LanguageProvider>
      </QueryClientProvider>
    </GoogleOAuthProvider>
  )
}

export default App
